import type { ReactNode } from 'react'
import type { CaptainEvent } from '../../types/captain'
import StationMarker, { type MarkerState } from './StationMarker'

interface Props {
  event: CaptainEvent
  markerState: MarkerState
  mine?: boolean
  isFirst?: boolean
  isLast?: boolean
  /** Optional content rendered to the right of the metadata block */
  children?: ReactNode
  /** DOM id, so deep links can scroll the card into view. */
  domId?: string
  /** ON-426: draws attention to the card a call-out link pointed at. */
  highlighted?: boolean
}

function StatusBadge({ state }: { state: MarkerState }) {
  if (state === 'past') return null
  const cfg =
    state === 'full'
      ? { bg: 'rgba(194,229,48,0.1)', border: 'rgba(194,229,48,0.4)', color: '#C2E530', label: 'Target met' }
      : state === 'partial'
      ? { bg: 'rgba(194,229,48,0.06)', border: 'rgba(194,229,48,0.3)', color: '#C2E530', label: 'Slots open' }
      : { bg: 'transparent', border: 'rgba(244,242,242,0.18)', color: 'rgba(244,242,242,0.7)', label: 'Slots open' }
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        padding: '3px 9px',
        borderRadius: 999,
        background: cfg.bg,
        border: `0.5px solid ${cfg.border}`,
        color: cfg.color,
        fontFamily: "'Helvetica Neue', 'HelveticaNeue', Helvetica, 'Inter', Arial, sans-serif",
        fontSize: 9,
        fontWeight: 600,
        letterSpacing: '0.14em',
        textTransform: 'uppercase',
      }}
    >
      <span
        style={{
          width: 5,
          height: 5,
          borderRadius: '50%',
          background: state === 'full' ? cfg.color : 'transparent',
          border: state !== 'full' ? `1px solid ${cfg.color}` : 'none',
        }}
      />
      {cfg.label}
    </span>
  )
}

function formatDate(iso: string) {
  const d = new Date(iso)
  const opts: Intl.DateTimeFormatOptions = { timeZone: 'Asia/Hong_Kong' }
  return {
    day: d.toLocaleString('en-GB', { day: '2-digit', ...opts }),
    month: d.toLocaleString('en-GB', { month: 'short', ...opts }).toUpperCase(),
    year: d.toLocaleString('en-GB', { year: 'numeric', ...opts }),
    weekday: d.toLocaleString('en-GB', { weekday: 'short', ...opts }).toUpperCase(),
    time: d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false, ...opts }),
  }
}

export default function EventStation({
  event,
  markerState,
  mine,
  isFirst,
  isLast,
  children,
  domId,
  highlighted,
}: Props) {
  const date = formatDate(event.date)
  // Only past events dim — 'full' (target met) is still an actionable,
  // open-for-overflow state, not a closed one (ON-334).
  const isPast = markerState === 'past'
  return (
    <article
      id={domId}
      data-highlighted={highlighted ? '' : undefined}
      className={highlighted ? 'cp-station-highlight' : undefined}
      style={{
        display: 'grid',
        gridTemplateColumns: '36px 1fr',
        gap: 24,
        position: 'relative',
        paddingBottom: 36,
      }}
    >
      {/* Rail column with marker + connecting line segments */}
      <div
        style={{
          position: 'relative',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'flex-start',
          paddingTop: 4,
        }}
      >
        {/* line above marker — terminates at marker top edge, never crosses through */}
        {!isFirst && (
          <span
            aria-hidden
            style={{
              position: 'absolute',
              top: 0,
              height: 4,
              left: '50%',
              width: 1,
              transform: 'translateX(-0.5px)',
              background: 'rgba(244,242,242,0.08)',
            }}
          />
        )}
        {/* line below marker, extending to next row's marker top edge */}
        {!isLast && (
          <span
            aria-hidden
            style={{
              position: 'absolute',
              top: 28,
              bottom: -36,
              left: '50%',
              width: 1,
              transform: 'translateX(-0.5px)',
              background: 'rgba(244,242,242,0.08)',
            }}
          />
        )}
        <StationMarker state={markerState} mine={mine} />
      </div>

      {/* Content column */}
      <div
        className="cp-station-content"
        style={{
          opacity: isPast ? 0.55 : 1,
          paddingTop: 2,
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 14,
            marginBottom: 6,
            flexWrap: 'wrap',
          }}
        >
          <div
            style={{
              fontFamily: "'Helvetica Neue', 'HelveticaNeue', Helvetica, 'Inter', Arial, sans-serif",
              fontSize: 11,
              color: '#C2E530',
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
              display: 'flex',
              alignItems: 'baseline',
              gap: 6,
            }}
          >
            <span>{date.weekday}</span>
            <span style={{ color: 'rgba(244,242,242,0.35)' }}>·</span>
            <span style={{ color: '#F4F2F2', fontSize: 13, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
              {date.day} {date.month} {date.year}
            </span>
            <span style={{ color: 'rgba(244,242,242,0.35)' }}>·</span>
            <span style={{ fontVariantNumeric: 'tabular-nums' }}>{date.time}</span>
          </div>
          <StatusBadge state={markerState} />
        </div>
        <h2
          style={{
            fontFamily: "'Helvetica Neue', 'HelveticaNeue', Helvetica, 'Inter', Arial, sans-serif",
            fontWeight: 700,
            fontSize: 26,
            lineHeight: 1.15,
            letterSpacing: '-0.01em',
            color: '#F4F2F2',
            margin: 0,
            marginBottom: 6,
          }}
        >
          {event.name}
        </h2>
        <div
          style={{
            fontFamily: "'Lora', Georgia, serif",
            fontSize: 14,
            color: 'rgba(198,196,185,0.7)',
            marginBottom: 18,
          }}
        >
          {event.location}
        </div>
        {children}
      </div>
    </article>
  )
}
